import React, { useState } from "react";
import { toast } from "sonner";
import { Mail, Unplug, Loader2, Save, RefreshCw, Settings2 } from "lucide-react";
import { api, errText, btnDark, btnLight, inputCls, labelCls } from "./api";

export function GmailCard({ gmail, onChanged, batchActive }) {
  const [busy, setBusy] = useState(false);
  const connect = async () => {
    setBusy(true);
    try {
      const { url } = await api.gmailConnect();
      window.location.href = url;
    } catch (e) { toast.error(errText(e)); setBusy(false); }
  };
  const disconnect = async () => {
    setBusy(true);
    try { await api.gmailDisconnect(); toast.success("Gmail disconnected"); onChanged(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className="bg-white border border-[#E8E1D2] p-5" data-testid="gmail-card">
      <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680] mb-2">Sending mailbox</div>
      {gmail.connected ? (
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-[13px] text-[#0F1825]" data-testid="gmail-connected">
            <span className="w-2 h-2 rounded-full bg-[#3F6D55]" /> <Mail size={14} /> {gmail.email}
          </span>
          <button type="button" onClick={disconnect} disabled={busy || batchActive} className={`${btnLight} ml-auto`} data-testid="gmail-disconnect" title={batchActive ? "Stop the sending batch first" : ""}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Unplug size={12} />} Disconnect
          </button>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-[13px] text-[#5C6470]" data-testid="gmail-disconnected">
            {gmail.configured
              ? "Not connected. Connect your Google Workspace mailbox — sent emails land in your Gmail Sent folder."
              : `Google OAuth is not configured on the server yet — missing: ${(gmail.missing || ["GOOGLE_CLIENT_ID", "GOOGLE_CLIENT_SECRET"]).join(", ")}. Add it in the deployment secrets and redeploy.`}
          </span>
          <button type="button" onClick={connect} disabled={busy || !gmail.configured} className={`${btnDark} ml-auto`} data-testid="gmail-connect">
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Connect Gmail
          </button>
        </div>
      )}
      {gmail.last_error && <div className="mt-2 text-[12px] text-[#C84C21]">{gmail.last_error}</div>}
    </div>
  );
}

export function CampaignCard({ campaign, counts, onChanged, onOpenSettings }) {
  const [name, setName] = useState(campaign.name || "");
  const [context, setContext] = useState(campaign.context || "");
  const [busy, setBusy] = useState(false);
  const [offerRedraft, setOfferRedraft] = useState(0);
  const dirty = context !== (campaign.context || "") || name !== (campaign.name || "");

  const save = async () => {
    setBusy(true);
    try {
      if (name !== campaign.name) await api.setName(name);
      const res = await api.setContext(context);
      toast.success("Campaign context saved");
      setOfferRedraft(res.unsent_drafts || 0);
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  const redraftAll = async () => {
    setBusy(true);
    try {
      await api.researchStart({ scope: "unsent", force: true });
      toast.success("Redrafting every unsent draft with the new context");
      setOfferRedraft(0);
      onChanged();
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-[#E8E1D2] p-5" data-testid="campaign-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[#A09680]">Current campaign · {counts.total} contacts · {counts.companies} companies</div>
        <button type="button" onClick={onOpenSettings} className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-[#5C6470] hover:text-[#C84C21]" data-testid="open-settings">
          <Settings2 size={12} /> Signature & sending settings
        </button>
      </div>
      <label className={labelCls} htmlFor="campaign-name">Campaign name</label>
      <input id="campaign-name" value={name} onChange={(e) => setName(e.target.value)} className={`${inputCls} mb-3`} data-testid="campaign-name" />
      <label className={labelCls} htmlFor="campaign-context">Campaign context · injected into every new draft and redraft</label>
      <textarea
        id="campaign-context"
        value={context}
        onChange={(e) => setContext(e.target.value)}
        rows={3}
        className={`${inputCls} resize-y`}
        placeholder='e.g. "I am attending ISPOR Europe in Barcelona 8–11 Nov — suggest meeting in person there."'
        data-testid="campaign-context"
      />
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button type="button" onClick={save} disabled={busy || !dirty} className={btnDark} data-testid="save-context">
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
        {offerRedraft > 0 && (
          <button type="button" onClick={redraftAll} disabled={busy} className={btnLight} data-testid="redraft-unsent">
            <RefreshCw size={12} /> Redraft all {offerRedraft} unsent drafts with this context
          </button>
        )}
      </div>
    </div>
  );
}
